import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Container, Grid, TextField, Typography, Paper,
  InputAdornment, IconButton, Alert, Collapse, List, ListItem,
  ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Chip
} from '@mui/material';
import {
  Visibility, VisibilityOff, ArrowBack, CheckCircle, Cancel,
  ErrorOutline, Save, Security, LockReset, Warning, Close as CloseIcon
} from '@mui/icons-material';

import Newfooter from '../NewFooter/Newfooter';
import { VIPSDataServiceURL, requestOptions } from '../../Constants/GlobalConstant';

// --- CONSTANTS FROM AJK THEME ---
const HEADER_BG = '#1e293b'; 
const PRIMARY_COLOR = '#1565c0'; 
const BUTTON_GRADIENT = 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)';

const dialogHeaderStyle = {
  backgroundColor: HEADER_BG, 
  color: '#ffffff !important', 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center', 
  padding: '16px 24px', 
  borderBottom: `4px solid ${PRIMARY_COLOR}`
};


const AuthService = {
  changePasswordProfile: async (userId, newPassword, securityQuestion, secretAnswer) => {
    const cleanUrl = VIPSDataServiceURL.replace(/\/$/, ""); 

    const queryParams = {
      userId: userId,
      newPassword: newPassword,
      secretQuestion: (securityQuestion && securityQuestion.trim() !== "") ? securityQuestion : '""',
      secretAnswer: (secretAnswer && secretAnswer.trim() !== "") ? secretAnswer : '""'
    };

    try {
      const response = await axios.post(
        `${cleanUrl}/User/UpdateUserSecurity`, 
        {}, // Empty body
        { 
          params: queryParams, // Arguments passed here
          headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } 
        }
      );
      return response.data;
    } catch (error) { throw error; }
  },
};

// --- HELPER TO DECODE HTML ENTITIES ---
const decodeHtmlEntities = (str) => {
    if (!str) return '';
    const txt = document.createElement("textarea");
    txt.innerHTML = str;
    return txt.value;
};

const ChangePassword = () => {
  const navigate = useNavigate();

  // 1. GET DATA FROM REDUX
  const userPost = useSelector((state) => state.user);
  const loggedInUserId = userPost?.paxWebSessionStrings?.find(
    (item) => item.Key === 'user_id'
  )?.Value || '';

  // 2. Form State
  const [formData, setFormData] = useState({
    userId: loggedInUserId,
    newPassword: '',
    confirmPassword: '',
    securityQuestion: '',
    secretAnswer: ''
  });

  const [minPassLength, setMinPassLength] = useState(8);
  const [passComplexityRegex, setPassComplexityRegex] = useState(null);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: '', msg: '' });
  const [openSecurityPopup, setOpenSecurityPopup] = useState(false);
  const [securityUpdatePending, setSecurityUpdatePending] = useState(false);
  
  // Dialog State
  const [dialogState, setDialogState] = useState({ open: false, title: '', message: '', isSuccess: false });
  const [strength, setStrength] = useState({ level: '', width: '0%', color: '' });
  const [passwordRules, setPasswordRules] = useState({ length: false, uppercase: false, number: false, special: false });

  // --- FETCH CONFIGURATION ---
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchParameter = async (paramName) => {
        try {
            const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
            const response = await axios.post(
                `${cleanUrl}/Misc/GetSystemParameterValueByName`, 
                {}, 
                { params: { name: paramName }, headers: { ...requestOptions.headers, 'Content-Type': 'application/json' }, signal }
            );
            if (response.data) {
                return String(response.data).replace(/^"|"$/g, '').replace(/(\r\n|\n|\r)/gm, "").trim();
            }
            return null;
        } catch (err) {
            if (!axios.isCancel(err)) console.warn(`[Config] Error fetching ${paramName}:`, err);
            return null;
        }
    };

    const loadConfig = async () => {
        const [len, passRegex] = await Promise.all([
            fetchParameter('MinimumPasswordLength'),
            fetchParameter('PasswordComplexity')
        ]);

        if (!signal.aborted) {
            if (len) setMinPassLength(Number(len));
            if (passRegex) {
                const decodedRegex = decodeHtmlEntities(passRegex);
                setPassComplexityRegex(decodedRegex);
            }
        }
    };

    loadConfig();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (loggedInUserId && formData.userId !== loggedInUserId) {
      setFormData(prev => ({ ...prev, userId: loggedInUserId }));
    }
  }, [loggedInUserId]);

  const setStatus = (type, msg) => setFormStatus({ type, msg });

  // --- CHECK STRENGTH (VISUAL) ---
  const checkStrength = (password) => {
    const lengthValid = password.length >= minPassLength;
    const uppercaseValid = /[A-Z]/.test(password);
    const numberValid = /[0-9]/.test(password);
    const specialValid = /[^A-Za-z0-9]/.test(password);

    let matchesServerRegex = true;
    if (passComplexityRegex) {
        try { 
            matchesServerRegex = new RegExp(passComplexityRegex).test(password); 
        } catch (e) { 
            matchesServerRegex = false; 
        }
    }

    setPasswordRules({ length: lengthValid, uppercase: uppercaseValid, number: numberValid, special: specialValid });

    if (!password) { setStrength({ level: '', width: '0%', color: '' }); return; }
    if (!lengthValid) { setStrength({ level: 'Weak', width: '25%', color: '#d32f2f' }); return; }

    const basicCount = [uppercaseValid, numberValid].filter(Boolean).length;
    
    if (basicCount === 2 && specialValid && matchesServerRegex) setStrength({ level: 'Perfect', width: '100%', color: '#2e7d32' });
    else if (basicCount === 2 && matchesServerRegex) setStrength({ level: 'Strong', width: '75%', color: '#0288d1' });
    else if (basicCount >= 1) setStrength({ level: 'Fair', width: '50%', color: '#ed6c02' });
    else setStrength({ level: 'Weak', width: '25%', color: '#d32f2f' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    let newErrors = { ...errors };
    if (newErrors[name]) delete newErrors[name];

    if (name === 'newPassword') {
        checkStrength(value);
        if (formData.confirmPassword && value !== formData.confirmPassword) newErrors.confirmPassword = "Passwords must match";
        else if (formData.confirmPassword && value === formData.confirmPassword) delete newErrors.confirmPassword;
    }
    if (name === 'confirmPassword') {
        if (formData.newPassword && value !== formData.newPassword) newErrors.confirmPassword = "Passwords must match";
        else delete newErrors.confirmPassword;
    }
    setErrors(newErrors);
  };

  const handleConfirmSecurity = () => {
    setSecurityUpdatePending(!!(formData.securityQuestion || formData.secretAnswer));
    setOpenSecurityPopup(false);
  };

  const handleRemoveSecurity = () => {
    setFormData(prev => ({ ...prev, securityQuestion: '', secretAnswer: '' }));
    setSecurityUpdatePending(false);
    setOpenSecurityPopup(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('', '');
    const currentErrors = {};

    if (!formData.userId) currentErrors.userId = "User ID is required";

    if (!formData.newPassword) {
        currentErrors.newPassword = "New Password is required";
    } else {
        if (formData.newPassword.length < minPassLength) {
            currentErrors.newPassword = `Minimum ${minPassLength} characters required`;
        } 
        else if (passComplexityRegex) {
            try {
                const regex = new RegExp(passComplexityRegex);
                if (!regex.test(formData.newPassword)) {
                    currentErrors.newPassword = "Password must contain an uppercase letter, a number, and a special character.";
                }
            } catch (error) {
                console.warn("Invalid regex from server:", error);
            }
        }
    }

    if (!formData.confirmPassword) currentErrors.confirmPassword = "Confirm Password is required";
    else if (formData.newPassword !== formData.confirmPassword) currentErrors.confirmPassword = "Passwords must match";

    if (Object.keys(currentErrors).length > 0) { 
        setErrors(currentErrors); 
        
        const errorList = Object.values(currentErrors).map(err => `• ${err}`).join('\n');
        setDialogState({ 
            open: true, 
            title: "Validation Error", 
            message: `Please correct the following errors:\n\n${errorList}`, 
            isSuccess: false 
        });
        return; 
    }

    setSubmitting(true);
    try {
        const response = await AuthService.changePasswordProfile(
            formData.userId, formData.newPassword,
            securityUpdatePending ? formData.securityQuestion : "",
            securityUpdatePending ? formData.secretAnswer : ""
        );
        const success = response?.success || response?.Success || response === true;
        setDialogState({ open: true, title: success ? "Success" : "Error", message: response?.message || response?.Message || "Password updated.", isSuccess: success });
    } catch (err) {
        setDialogState({ open: true, title: "Error", message: err.response?.data?.message || err.message || 'Update failed', isSuccess: false });
    } finally { setSubmitting(false); }
  };

  // Helper for icons (Styled for the dark AJK header)
  const getDialogIcon = () => {
      if (dialogState.isSuccess) return <CheckCircle sx={{ color: '#ffffff !important' }} />;
      if (dialogState.title === "Validation Error") return <Warning sx={{ color: '#ffffff !important' }} />;
      return <ErrorOutline sx={{ color: '#ffffff !important' }} />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pt: 4, pb: 8 }}>
        <Container maxWidth="md">
          <Paper elevation={6} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, position: 'relative' }}>
            
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#222', mb: 1 }}>Change Password</Typography>
              <Box sx={{ width: '60px', height: '4px', backgroundColor: '#d32f2f', mx: 'auto', mb: 2, borderRadius: 2 }} />
            </Box>

            <Collapse in={!!formStatus.msg}>
              <Alert severity={formStatus.type === 'success' ? 'success' : 'error'} onClose={() => setStatus('', '')} sx={{ mb: 4 }}>{formStatus.msg}</Alert>
            </Collapse>

            <form onSubmit={handleSubmit} autoComplete="off">
              <Grid container spacing={3}>
                
                {/* User ID */}
                <Grid item xs={12} md={4} sx={{ pt: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#d32f2f' }}>User ID*:</Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                    <TextField fullWidth size="small" name="userId" value={formData.userId} InputProps={{ readOnly: true, sx: { borderRadius: 1.5, bgcolor: '#f5f5f5' } }} />
                </Grid>

                {/* New Password */}
                <Grid item xs={12} md={4} sx={{ pt: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#d32f2f' }}>New Password*:</Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                    <TextField 
                        fullWidth 
                        type={showPassword ? 'text' : 'password'} 
                        size="small" 
                        name="newPassword" 
                        value={formData.newPassword} 
                        onChange={handleChange} 
                        error={!!errors.newPassword} 
                        helperText={errors.newPassword} 
                        autoComplete="new-password"
                        InputProps={{ 
                            sx: { borderRadius: 1.5 }, 
                            endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} size="small">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) 
                        }} 
                    />
                    <Collapse in={!!strength.level}>
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#fff', border: '1px solid #eee', borderRadius: 2 }}>
                                <Box sx={{ height: 6, width: '100%', bgcolor: '#f0f0f0', borderRadius: 5, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: strength.width, bgcolor: strength.color, transition: 'width 0.3s' }} />
                                </Box>
                                <Typography variant="caption" sx={{ color: strength.color, fontWeight: 'bold', display:'block', mt:1 }}>{strength.level}</Typography>
                                <List dense sx={{ p: 0, mt: 1 }}>
                                    <StrengthItem label={`Minimum ${minPassLength} characters`} valid={passwordRules.length} />
                                    <StrengthItem label="One uppercase letter" valid={passwordRules.uppercase} />
                                    <StrengthItem label="One number" valid={passwordRules.number} />
                                    <StrengthItem label="One special character" valid={passwordRules.special} />
                                </List>
                        </Box>
                    </Collapse>
                </Grid>

                {/* Confirm Password */}
                <Grid item xs={12} md={4} sx={{ pt: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#d32f2f' }}>Confirm Password*:</Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                    <TextField 
                        fullWidth 
                        type={showConfirm ? 'text' : 'password'} 
                        size="small" 
                        name="confirmPassword" 
                        value={formData.confirmPassword} 
                        onChange={handleChange} 
                        error={!!errors.confirmPassword} 
                        helperText={errors.confirmPassword} 
                        autoComplete="new-password"
                        InputProps={{ sx: { borderRadius: 1.5 }, endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowConfirm(!showConfirm)} size="small">{showConfirm ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }} 
                    />
                </Grid>

                {/* Security Settings */}
                <Grid item xs={12} md={4} sx={{ pt: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#555' }}>Security Settings:</Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                    {!securityUpdatePending ? (
                        <Button variant="outlined" startIcon={<Security />} onClick={() => setOpenSecurityPopup(true)} sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#ccc', color: '#666' }}>Update Security Question (Optional)</Button>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip icon={<CheckCircle />} label="Security Question Updated" color="success" variant="outlined" />
                            <Button size="small" color="error" onClick={() => setOpenSecurityPopup(true)}>Edit</Button>
                        </Box>
                    )}
                </Grid>

                {/* Buttons */}
                <Grid item xs={12} md={4} />
                <Grid item xs={12} md={8}>
                    <Button type="submit" variant="contained" disabled={submitting} fullWidth startIcon={!submitting && <Save />} sx={{ py: 1.5, fontWeight: 'bold', fontSize: "16px", borderRadius: 2, mt: 2, background: BUTTON_GRADIENT }}>{submitting ? "Updating..." : "Update Password"}</Button>
                    <Button variant="outlined" fullWidth onClick={() => navigate('/dashboard')} startIcon={<ArrowBack />} sx={{ mt: 2, py: 1.2, borderRadius: 2, borderColor: '#ccc', color: '#555' }}>Back</Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>

      {/* --- UPDATED Security Dialog (AJK Theme) --- */}
      <Dialog 
        open={openSecurityPopup} 
        onClose={() => setOpenSecurityPopup(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      >
        <DialogTitle sx={dialogHeaderStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LockReset fontSize="medium" sx={{ color: '#ffffff !important' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff !important', letterSpacing: '0.5px' }}>
                    Update Security Question
                </Typography>
            </Box>
            <IconButton onClick={() => setOpenSecurityPopup(false)} sx={{ color: '#ffffff !important' }}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#fafafa', py: 4 }} dividers>
            <DialogContentText sx={{ mb: 3 }}>Please enter a new security question and answer.</DialogContentText>
            <Grid container spacing={3}>
                <Grid item xs={12}><TextField fullWidth label="Security Question" name="securityQuestion" value={formData.securityQuestion} onChange={handleChange} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Security Answer" name="secretAnswer" value={formData.secretAnswer} onChange={handleChange} /></Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#f4f2e9', p: 3, borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={handleRemoveSecurity} color="error" sx={{ mr: 'auto', fontWeight: 600, textTransform: 'none' }}>Clear</Button>
            <Button onClick={() => setOpenSecurityPopup(false)} sx={{ color: '#64748b', fontWeight: 600, mr: 1, textTransform: 'none' }}>Close</Button>
            <Button onClick={handleConfirmSecurity} variant="contained" sx={{ background: BUTTON_GRADIENT, px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* --- UPDATED Result / Error Dialog (AJK Theme) --- */}
      <Dialog 
        open={dialogState.open} 
        onClose={() => setDialogState(prev => ({...prev, open: false}))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      >
        <DialogTitle sx={dialogHeaderStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {getDialogIcon()}
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff !important', letterSpacing: '0.5px' }}>
                    {dialogState.title}
                </Typography>
            </Box>
            <IconButton 
                onClick={() => { 
                    setDialogState(prev => ({...prev, open: false})); 
                    if(dialogState.isSuccess) navigate('/dashboard'); 
                }} 
                sx={{ color: '#ffffff !important' }}
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ backgroundColor: '#fafafa', py: 4 }} dividers>
            <DialogContentText sx={{ color: '#334155', fontWeight: 500, fontSize: '1rem', whiteSpace: 'pre-line' }}>
                {dialogState.message}
            </DialogContentText>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f4f2e9', p: 3, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
            <Button 
                onClick={() => { 
                    setDialogState(prev => ({...prev, open: false})); 
                    if(dialogState.isSuccess) navigate('/dashboard'); 
                }} 
                variant="contained" 
                autoFocus 
                sx={{ background: BUTTON_GRADIENT, px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
                Close
            </Button>
        </DialogActions>
      </Dialog>
      <Newfooter/>
    </Box>
  );
};

const StrengthItem = ({ label, valid }) => (
  <ListItem sx={{ py: 0, px: 0 }}><ListItemIcon sx={{ minWidth: 24 }}>{valid ? <CheckCircle fontSize="inherit" color="success" sx={{ fontSize: 16 }} /> : <Cancel fontSize="inherit" color="disabled" sx={{ fontSize: 16 }} />}</ListItemIcon><ListItemText primary={label} primaryTypographyProps={{ variant: 'caption', color: valid ? 'text.primary' : 'text.disabled' }} /></ListItem>
);

export default ChangePassword;